import React, { useMemo, useState } from "react";
import NumberFormat from "react-number-format";

import { Button, Divider, MenuItem, Stack, TextField } from "@mui/material";
import { DateQuarterPicker } from "components/forms";
import { ProgressOverlay } from "components/Progress-overlay/ProgressOverlay";
import { ValidationError } from "components/Validation-error/ValidationError";
import dayjs from "dayjs";
import { useModals } from "hooks";
import { ApiServiceError, IApiServiceError, useDealActions, useDealManagementFeeSettingSelect } from "store";

import { Errors } from "../Groups-form/GroupsFormContext";

interface Props {
  dealId: number;
  onSubmit: (state: ValidState) => Promise<void>;
  onClose: () => void;
  initialState?: State;
}

type FeeOptionType = { key: "contract" | "report" | "previous-period"; title: string };

type State = {
  source: FeeOptionType["key"] | null;
  amount: string;
  from_date: string | null;
  to_date: string | null;
};

export type ValidState = {
  source: FeeOptionType["key"];
  amount: string;
  from_date: string;
  to_date: string;
};

const defaultState: State = { source: null, amount: "", from_date: null, to_date: null };
const APIAcceptableDateFormat = "YYYY-MM-DD";

export const Form: React.FC<Props> = ({ onSubmit, dealId, onClose, initialState = defaultState }) => {
  const { commitment_fees_inv: commitFeesInvestPeriod } = useDealManagementFeeSettingSelect() || {};
  const { getMeasureFeesAmountAction } = useDealActions();
  const { errorModal } = useModals();

  const feeOptions: Array<FeeOptionType> = [
    { key: "contract", title: `From Сontract ${commitFeesInvestPeriod ? `${commitFeesInvestPeriod}%` : ""}` },
    { key: "report", title: "From Report (manual)" },
    { key: "previous-period", title: "From Previous Period" }
  ];

  const [state, setState] = useState<State>(initialState);
  const [isAmountLoading, setIsAmountLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dateRangeError, setDateRangeError] = useState<string>("");

  function setFieldValue<F extends keyof State>({ field, value }: { field: F; value: State[F] }) {
    setState((prevState) => ({ ...prevState, [field]: value }));
  }

  const isSubmitDisabled: boolean = useMemo(() => {
    return Object.values(state).some((item) => Boolean(item) === false);
  }, [state]);

  const handleChangeManageFeeSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const calcType = e.target.value as FeeOptionType["key"];

      setFieldValue({ field: "source", value: calcType });

      if (calcType !== "report") {
        setIsAmountLoading(true);

        const amount = await getMeasureFeesAmountAction({ source: calcType, deal_id: dealId });
        setFieldValue({ field: "amount", value: amount });
      }
    } catch (e) {
      errorModal({ msg: (e as ApiServiceError).message });
    } finally {
      setIsAmountLoading(false);
    }
  };

  const validate = (state: State): ValidState => {
    if (!state.source) throw Errors.NO_SOURCE;
    if (!state.from_date) throw Errors.NO_FROM_DATE;
    if (!state.to_date) throw Errors.NO_TO_DATE;
    return state as ValidState;
  };

  const clearDateRangeError = () => {
    if (!dateRangeError) return;
    setDateRangeError("");
  };

  const validateDateRange = (fromDate: string | null, toDate: string | null) => {
    if (fromDate && toDate) {
      const from = dayjs(fromDate);
      const to = dayjs(toDate);
      if (from.isAfter(to)) {
        setDateRangeError("From date must be before to date");
      } else {
        clearDateRangeError();
      }
    }
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const validatedState = validate(state);
      await onSubmit({
        ...validatedState,
        from_date: dayjs(validatedState.from_date).format(APIAcceptableDateFormat),
        to_date: dayjs(validatedState.to_date).format(APIAcceptableDateFormat)
      });
    } catch (err) {
      if ((err as IApiServiceError)?.errors?.["date_range"]) {
        setDateRangeError((err as IApiServiceError).errors.date_range?.[0]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getQuartalDates = (dateString: string) => {
    // example dateString: "2024-01-01"
    if (!dateString) {
      return {
        start: null,
        end: null
      };
    }
    const date = new Date(dateString);
    const month = date.getMonth();
    const year = date.getFullYear();

    const quarters = [
      { start: "01-01", end: "03-31" },
      { start: "04-01", end: "06-30" },
      { start: "07-01", end: "09-30" },
      { start: "10-01", end: "12-31" }
    ];

    const quartalIndex = Math.floor(month / 3);

    return {
      start: `${year}-${quarters[quartalIndex].start}`,
      end: `${year}-${quarters[quartalIndex].end}`
    };
  };

  const firstQuartal = useMemo(() => {
    return getQuartalDates(state.from_date || "");
  }, [state.from_date]);

  const lastQuartal = useMemo(() => {
    return getQuartalDates(state.to_date || "");
  }, [state.to_date]);

  return (
    <>
      <ProgressOverlay
        containerSX={{ display: "flex", flexDirection: "column", flexGrow: 1, gap: "20px", p: "20px" }}
        inProgress={isLoading}
      >
        <TextField
          select
          label="Management Fee"
          placeholder="Select"
          value={state.source}
          onChange={handleChangeManageFeeSelect}
        >
          {feeOptions.map((option) => (
            <MenuItem
              {...(option.key === "contract" && { disabled: !commitFeesInvestPeriod })}
              key={option.key}
              value={option.key}
            >
              {option.title}
            </MenuItem>
          ))}
        </TextField>
        <ProgressOverlay inProgress={isAmountLoading}>
          <NumberFormat
            size="small"
            customInput={TextField}
            label="Sum"
            {...(state.source &&
              state.source !== "report" && {
                sx: { ".MuiInputBase-root": { color: "#97A6BF" }, ".MuiFormLabel-root": { color: "#97A6BF" } }
              })}
            value={state.amount}
            disabled={state.source !== "report"}
            placeholder="Empty"
            onValueChange={({ value }) => setFieldValue({ field: "amount", value: value })}
            decimalScale={2}
            fullWidth
            thousandSeparator
            isNumericString
          />
        </ProgressOverlay>

        <DateQuarterPicker
          label="From Date"
          startDate={firstQuartal?.start}
          endDate={firstQuartal?.end}
          onChange={(data) => {
            if (!data) {
              setFieldValue({ field: "from_date", value: null });
              return;
            }
            clearDateRangeError();
            setFieldValue({ field: "from_date", value: data.start });
            validateDateRange(data.start, state.to_date);
          }}
        />
        <DateQuarterPicker
          label="To Date"
          startDate={lastQuartal?.start}
          endDate={lastQuartal?.end}
          onChange={(data) => {
            if (!data) {
              setFieldValue({ field: "to_date", value: null });
              return;
            }
            clearDateRangeError();
            setFieldValue({ field: "to_date", value: data.end });
            validateDateRange(state.from_date, data.end);
          }}
        />
        {dateRangeError && <ValidationError messages={[dateRangeError]} />}
        <Divider sx={{ borderColor: "#DBE8F5" }} />
      </ProgressOverlay>
      <Stack
        sx={{
          flexDirection: "row",
          justifyContent: "flex-end",
          p: "20px",
          boxShadow: "0px -4px 13px 0px rgba(10, 49, 66, 0.20)",
          gap: "20px"
        }}
      >
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSubmitDisabled || Boolean(dateRangeError)} variant="contained" onClick={handleSubmit}>
          Add
        </Button>
      </Stack>
    </>
  );
};
